// Small WebGL2 helpers: programs, textures, framebuffers, the fullscreen triangle. No framework.

export type GL = WebGL2RenderingContext;

export const VERT = `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
  // Fullscreen triangle from gl_VertexID; vUv = 0..1 over the viewport, y up.
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

export class GlError extends Error {}

function compile(gl: GL, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(s);
    const numbered = src
      .split('\n')
      .map((l, i) => `${String(i + 1).padStart(4)} ${l}`)
      .join('\n');
    gl.deleteShader(s);
    throw new GlError(`shader compile failed: ${log}\n${numbered}`);
  }
  return s;
}

export class Program {
  readonly prog: WebGLProgram;
  private locs = new Map<string, WebGLUniformLocation | null>();
  constructor(
    readonly gl: GL,
    frag: string,
    vert = VERT,
  ) {
    const p = gl.createProgram()!;
    const vs = compile(gl, gl.VERTEX_SHADER, vert);
    const fs = compile(gl, gl.FRAGMENT_SHADER, frag);
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS) && !gl.isContextLost())
      throw new GlError(`link failed: ${gl.getProgramInfoLog(p)}`);
    this.prog = p;
  }
  use() {
    this.gl.useProgram(this.prog);
    return this;
  }
  loc(name: string) {
    if (!this.locs.has(name)) this.locs.set(name, this.gl.getUniformLocation(this.prog, name));
    return this.locs.get(name)!;
  }
  f(name: string, ...v: number[]) {
    const l = this.loc(name);
    if (!l) return this;
    const gl = this.gl;
    if (v.length === 1) gl.uniform1f(l, v[0]!);
    else if (v.length === 2) gl.uniform2f(l, v[0]!, v[1]!);
    else if (v.length === 3) gl.uniform3f(l, v[0]!, v[1]!, v[2]!);
    else gl.uniform4f(l, v[0]!, v[1]!, v[2]!, v[3]!);
    return this;
  }
  i(name: string, v: number) {
    const l = this.loc(name);
    if (l) this.gl.uniform1i(l, v);
    return this;
  }
  m3(name: string, m: Float32Array) {
    const l = this.loc(name);
    if (l) this.gl.uniformMatrix3fv(l, false, m);
    return this;
  }
  tex(name: string, unit: number, t: WebGLTexture, target: number = this.gl.TEXTURE_2D) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(target, t);
    return this.i(name, unit);
  }
  dispose() {
    this.gl.deleteProgram(this.prog);
  }
}

export type TexFormat = 'rgba8' | 'rgba16f' | 'r16f' | 'rgba32f' | 'r8';

export function createTexture(
  gl: GL,
  w: number,
  h: number,
  fmt: TexFormat,
  data: ArrayBufferView | null = null,
  filter: 'linear' | 'nearest' = 'linear',
): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  const [internal, format, type] =
    fmt === 'rgba8'
      ? [gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE]
      : fmt === 'rgba16f'
        ? [gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT]
        : fmt === 'rgba32f'
          ? [gl.RGBA32F, gl.RGBA, gl.FLOAT]
          : fmt === 'r8'
            ? [gl.R8, gl.RED, gl.UNSIGNED_BYTE]
            : [gl.R16F, gl.RED, gl.HALF_FLOAT];
  gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, data);
  const f = filter === 'linear' ? gl.LINEAR : gl.NEAREST;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

/** A float render target. */
export class Target {
  readonly fbo: WebGLFramebuffer;
  readonly tex: WebGLTexture;
  constructor(
    readonly gl: GL,
    readonly width: number,
    readonly height: number,
    fmt: TexFormat = 'rgba16f',
  ) {
    this.tex = createTexture(gl, width, height, fmt);
    this.fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.tex, 0);
    const st = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (st !== gl.FRAMEBUFFER_COMPLETE && !gl.isContextLost())
      throw new GlError(`framebuffer incomplete: 0x${st.toString(16)}`);
  }
  bind() {
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.fbo);
    this.gl.viewport(0, 0, this.width, this.height);
  }
  dispose() {
    this.gl.deleteFramebuffer(this.fbo);
    this.gl.deleteTexture(this.tex);
  }
}

let vao: WebGLVertexArrayObject | null = null;
let vaoGl: GL | null = null;
export function drawFullscreen(gl: GL) {
  if (vaoGl !== gl) {
    vao = gl.createVertexArray();
    vaoGl = gl;
  }
  gl.bindVertexArray(vao);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}

/** Reads a float target as RGBA Float32 (row 0 = bottom, as WebGL stores it). */
export function readFloat(gl: GL, t: Target, x = 0, y = 0, w = t.width, h = t.height): Float32Array {
  t.bind();
  const out = new Float32Array(w * h * 4);
  gl.readPixels(x, y, w, h, gl.RGBA, gl.FLOAT, out);
  return out;
}

/** Reads an RGBA16F target as half-float bits (only when IMPLEMENTATION_COLOR_READ_TYPE is HALF_FLOAT). */
export function readHalf(gl: GL, t: Target, x = 0, y = 0, w = t.width, h = t.height): Uint16Array {
  t.bind();
  const out = new Uint16Array(w * h * 4);
  gl.readPixels(x, y, w, h, gl.RGBA, gl.HALF_FLOAT, out);
  return out;
}

export function enableFloat(gl: GL) {
  gl.getExtension('EXT_color_buffer_float');
  gl.getExtension('EXT_color_buffer_half_float');
  gl.getExtension('OES_texture_float_linear');
}
